/**
 * WeaveIn.ai Platform - Campaign Templates Management
 * Manage templates for marketing campaigns (Email, SMS, WhatsApp, Voice, Social)
 * Includes multi-channel and personalization variable support
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, MessageSquare, Phone, Share2, Plus, Search, Filter, Edit, Trash2,
  Copy, Eye, ChevronLeft, Save, X, AlertCircle, Check, Globe, Clock
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useTemplateVariables, insertVariableAtCursor } from '@/hooks/useTemplateVariables';
import { coldOutreachApi, EmailTemplate } from '@/src/platform/shared/services/platformApi';

// Channel types for campaign templates
type ChannelType = 'email' | 'sms' | 'whatsapp' | 'voice' | 'social';

// Template categories
type TemplateCategory = 'promotional' | 'transactional' | 'nurture' | 'announcement' | 'event' | 'newsletter';

interface CampaignTemplate {
  id: string;
  name: string;
  description: string | null;
  category: TemplateCategory;
  channel: ChannelType;
  subject: string | null;
  preview_text: string | null;
  body: string;
  html_body: string | null;
  variable_fields: string[];
  is_active: boolean;
  cbo_id: number | null;
  created_at: string;
  updated_at: string;
  created_by: number;
  created_by_name: string | null;
  use_count: number;
  is_custom?: boolean;
}

interface CampaignTemplatesProps {
  onBack?: () => void;
}

const CHANNEL_CONFIG = {
  email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  whatsapp: { label: 'WhatsApp', icon: MessageSquare, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  voice: { label: 'Voice', icon: Phone, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  social: { label: 'Social', icon: Share2, color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300' },
};

const CATEGORY_CONFIG = {
  promotional: { label: 'Promotional', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' },
  transactional: { label: 'Transactional', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
  nurture: { label: 'Nurture', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' },
  announcement: { label: 'Announcement', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
  event: { label: 'Event', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300' },
  newsletter: { label: 'Newsletter', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300' },
};

// Variables fetched from API via useTemplateVariables hook

const CampaignTemplates: React.FC<CampaignTemplatesProps> = ({ onBack }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { fields: availableFields } = useTemplateVariables();
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Map a cold-outreach library template into this component's shape
  const TYPE_TO_CATEGORY: Record<string, TemplateCategory> = {
    cold_email: 'promotional',
    follow_up: 'nurture',
    breakup: 'transactional',
    linkedin_message: 'announcement',
    linkedin_connection: 'announcement',
  };
  const CATEGORY_TO_TYPE: Record<string, string> = {
    promotional: 'cold_email',
    nurture: 'follow_up',
    transactional: 'breakup',
    announcement: 'cold_email',
    event: 'cold_email',
    newsletter: 'cold_email',
  };

  const mapFromApi = useCallback((t: EmailTemplate): CampaignTemplate => ({
    id: t.id,
    name: t.template_name,
    description: t.description,
    category: TYPE_TO_CATEGORY[t.template_type] || 'promotional',
    channel: t.template_type.startsWith('linkedin') ? 'social' : 'email',
    subject: t.subject_line,
    preview_text: null,
    body: t.body_content,
    html_body: null,
    variable_fields: t.personalization_tokens || [],
    is_active: t.is_active,
    cbo_id: null,
    created_at: t.created_at,
    updated_at: t.updated_at,
    created_by: 0,
    created_by_name: t.is_custom ? null : 'Template Library',
    use_count: 0,
    is_custom: t.is_custom,
  }), []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await coldOutreachApi.getTemplates();
      if (resp.success && resp.data) {
        setTemplates((resp.data.templates || []).map(mapFromApi));
      } else {
        setError(resp.error || 'Failed to load templates');
      }
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [mapFromApi]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChannel, setFilterChannel] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CampaignTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<CampaignTemplate | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'promotional' as TemplateCategory,
    channel: 'email' as ChannelType,
    subject: '',
    preview_text: '',
    body: '',
    variable_fields: [] as string[],
    is_active: true,
  });

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesChannel = filterChannel === 'all' || template.channel === filterChannel;
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    return matchesSearch && matchesChannel && matchesCategory;
  });

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      category: 'promotional',
      channel: 'email',
      subject: '',
      preview_text: '',
      body: '',
      variable_fields: [],
      is_active: true,
    });
    setEditingTemplate(null);
  }, []);

  // Open create modal
  const handleCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  // Open edit modal
  const handleEdit = (template: CampaignTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category,
      channel: template.channel,
      subject: template.subject || '',
      preview_text: template.preview_text || '',
      body: template.body,
      variable_fields: template.variable_fields,
      is_active: template.is_active,
    });
    setShowCreateModal(true);
  };

  // Preview template
  const handlePreview = (template: CampaignTemplate) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  // Duplicate template — creates a tenant-owned copy via the API
  const handleDuplicate = async (template: CampaignTemplate) => {
    try {
      const resp = await coldOutreachApi.createTemplate({
        template_name: `${template.name} (Copy)`,
        template_type: CATEGORY_TO_TYPE[template.category] || 'cold_email',
        description: template.description || undefined,
        subject_line: template.subject || undefined,
        body_content: template.body,
      });
      if (resp.success) {
        loadTemplates();
      } else {
        alert(`Failed to duplicate template: ${resp.error || 'unknown error'}`);
      }
    } catch (err) {
      console.error('Duplicate failed:', err);
      alert('Failed to duplicate template');
    }
  };

  // Delete template (custom templates only)
  const handleDelete = async (templateId: string) => {
    const target = templates.find(t => t.id === templateId);
    if (target && !target.is_custom) {
      alert('Library templates cannot be deleted. Duplicate one to make an editable copy.');
      return;
    }
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      const resp = await coldOutreachApi.deleteTemplate(templateId);
      if (resp.success) {
        setTemplates(templates.filter(t => t.id !== templateId));
      } else {
        alert(`Failed to delete template: ${resp.error || 'unknown error'}`);
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete template');
    }
  };

  // Save template (create custom, or update an existing custom one)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a template name');
      return;
    }
    if (!formData.body.trim()) {
      alert('Please enter template content');
      return;
    }

    try {
      if (editingTemplate) {
        if (!editingTemplate.is_custom) {
          alert('Library templates are read-only. Use Duplicate to create an editable copy.');
          return;
        }
        const resp = await coldOutreachApi.updateTemplate(editingTemplate.id, {
          template_name: formData.name,
          template_type: CATEGORY_TO_TYPE[formData.category] || 'cold_email',
          description: formData.description || undefined,
          subject_line: formData.subject || undefined,
          body_content: formData.body,
          is_active: formData.is_active,
        });
        if (!resp.success) {
          alert(`Failed to update template: ${resp.error || 'unknown error'}`);
          return;
        }
      } else {
        const resp = await coldOutreachApi.createTemplate({
          template_name: formData.name,
          template_type: CATEGORY_TO_TYPE[formData.category] || 'cold_email',
          description: formData.description || undefined,
          subject_line: formData.subject || undefined,
          body_content: formData.body,
        });
        if (!resp.success) {
          alert(`Failed to create template: ${resp.error || 'unknown error'}`);
          return;
        }
      }
      loadTemplates();
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save template');
    }
  };

  // Insert variable at cursor position in body
  const insertVariable = (variableName: string) => {
    const newBody = insertVariableAtCursor(bodyTextareaRef, formData.body, variableName);
    setFormData(prev => ({
      ...prev,
      body: newBody,
      variable_fields: prev.variable_fields.includes(variableName)
        ? prev.variable_fields
        : [...prev.variable_fields, variableName],
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-paper-2 rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Go back"
            >
              <ChevronLeft className="w-6 h-6 text-ink-500" />
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 active:scale-95 transition-all min-h-[44px] flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('admin.campaignTemplates.createTemplate', 'Create Template')}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('admin.campaignTemplates.searchPlaceholder', 'Search templates...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Channel Filter */}
            <div>
              <select
                value={filterChannel}
                onChange={(e) => setFilterChannel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('admin.campaignTemplates.allChannels', 'All Channels')}</option>
                {Object.entries(CHANNEL_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('admin.campaignTemplates.allCategories', 'All Categories')}</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('admin.campaignTemplates.loading', 'Loading templates...')}</p>
          </div>
        ) : error ? (
          <div className="border rounded-xl p-8 text-center bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <AlertCircle className="mx-auto h-16 w-16 mb-4 text-red-400 dark:text-red-500" />
            <h3 className="text-lg font-semibold mb-2 text-red-900 dark:text-red-100">{t('admin.campaignTemplates.errorLoading', 'Error Loading Templates')}</h3>
            <p className="mb-6 text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('admin.campaignTemplates.tryAgain', 'Try Again')}
            </button>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Mail className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('admin.campaignTemplates.noTemplates', 'No templates found')}</p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              {t('admin.campaignTemplates.createFirst', 'Create Your First Template')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredTemplates.map((template) => {
              const channelConfig = CHANNEL_CONFIG[template.channel];
              const categoryConfig = CATEGORY_CONFIG[template.category];
              const ChannelIcon = channelConfig.icon;

              return (
                <div
                  key={template.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{template.name}</h3>
                        {!template.is_active && (
                          <span className="px-2 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                            {t('admin.campaignTemplates.inactive', 'Inactive')}
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center gap-1 ${channelConfig.color}`}>
                          <ChannelIcon className="w-3 h-3" />
                          {channelConfig.label}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${categoryConfig.color}`}>
                          {categoryConfig.label}
                        </span>
                      </div>

                      {template.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{template.description}</p>
                      )}

                      {template.subject && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          <span className="font-medium">{t('admin.campaignTemplates.subject', 'Subject')}:</span> {template.subject}
                        </p>
                      )}

                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">{template.body}</p>

                      {template.variable_fields.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('admin.campaignTemplates.variables', 'Variables')}:</span>
                          {template.variable_fields.map((field) => (
                            <span
                              key={field}
                              className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                            >
                              {`{{${field}}}`}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {fmt.short(template.created_at)}
                        </span>
                        <span>{t('admin.campaignTemplates.usedTimes', 'Used {{count}} times', { count: template.use_count })}</span>
                        <span>{t('admin.campaignTemplates.by', 'By')} {template.created_by_name || t('admin.campaignTemplates.unknown', 'Unknown')}</span>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePreview(template)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title={t('admin.campaignTemplates.preview', 'Preview')}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title={t('admin.campaignTemplates.duplicate', 'Duplicate')}
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                        title={t('admin.campaignTemplates.edit', 'Edit')}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                        title={t('admin.campaignTemplates.delete', 'Delete')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
            resetForm();
          }
        }}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? t('admin.campaignTemplates.editTemplate', 'Edit Template') : t('admin.campaignTemplates.createNewTemplate', 'Create New Template')}
              </DialogTitle>
              <DialogDescription>
                {editingTemplate
                  ? t('admin.campaignTemplates.updateDetails', 'Update the template details below')
                  : t('admin.campaignTemplates.fillDetails', 'Fill in the details to create a new campaign template')}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.campaignTemplates.templateName', 'Template Name')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder={t('admin.campaignTemplates.templateNamePlaceholder', 'e.g., Welcome Email')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.campaignTemplates.channel', 'Channel')} <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={formData.channel}
                    onChange={(e) => setFormData({ ...formData, channel: e.target.value as ChannelType })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {Object.entries(CHANNEL_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.campaignTemplates.category', 'Category')} <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as TemplateCategory })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 text-teal-600 border-gray-300 dark:border-gray-600 rounded focus:ring-teal-500 bg-white dark:bg-gray-700"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.campaignTemplates.active', 'Active')}</span>
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.campaignTemplates.descriptionLabel', 'Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder={t('admin.campaignTemplates.descriptionPlaceholder', 'Brief description of when to use this template')}
                />
              </div>

              {/* Subject (for email) */}
              {formData.channel === 'email' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.campaignTemplates.emailSubject', 'Email Subject')} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={t('admin.campaignTemplates.subjectPlaceholder', 'Use {{variable_name}} for personalization')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.campaignTemplates.previewText', 'Preview Text')}
                    </label>
                    <input
                      type="text"
                      value={formData.preview_text}
                      onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={t('admin.campaignTemplates.previewTextPlaceholder', 'Shows in email preview (optional)')}
                    />
                  </div>
                </>
              )}

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.campaignTemplates.messageContent', 'Message Content')} <span className="text-red-600">*</span>
                </label>
                <textarea
                  ref={bodyTextareaRef}
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder={t('admin.campaignTemplates.bodyPlaceholder', 'Use {{variable_name}} for dynamic content')}
                />
              </div>

              {/* Available Variables */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.campaignTemplates.personalizationVariables', 'Personalization Variables (Click to insert)')}
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  {availableFields.map((field) => (
                    <button
                      key={field.field_name}
                      type="button"
                      onClick={() => insertVariable(field.field_name)}
                      className="px-3 py-1.5 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-200 dark:border-gray-500"
                      title={field.field_label}
                    >
                      {`{{${field.field_name}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {t('admin.campaignTemplates.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingTemplate ? t('admin.campaignTemplates.updateTemplate', 'Update Template') : t('admin.campaignTemplates.createTemplate', 'Create Template')}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('admin.campaignTemplates.templatePreview', 'Template Preview')}</DialogTitle>
              <DialogDescription>
                {t('admin.campaignTemplates.previewDescription', 'Preview how this template will look with sample data')}
              </DialogDescription>
            </DialogHeader>

            {previewTemplate && (
              <div className="py-4 space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${CHANNEL_CONFIG[previewTemplate.channel].color}`}>
                    {CHANNEL_CONFIG[previewTemplate.channel].label}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${CATEGORY_CONFIG[previewTemplate.category].color}`}>
                    {CATEGORY_CONFIG[previewTemplate.category].label}
                  </span>
                </div>

                {previewTemplate.subject && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t('admin.campaignTemplates.subject', 'Subject')}</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {previewTemplate.subject
                        .replace(/\{\{first_name\}\}/g, 'John')
                        .replace(/\{\{organization_name\}\}/g, 'Acme Corp')
                        .replace(/\{\{current_date\}\}/g, fmt.short(new Date()))}
                    </p>
                  </div>
                )}

                <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t('admin.campaignTemplates.content', 'Content')}</p>
                  <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                    {previewTemplate.body
                      .replace(/\{\{first_name\}\}/g, 'John')
                      .replace(/\{\{last_name\}\}/g, 'Doe')
                      .replace(/\{\{full_name\}\}/g, 'John Doe')
                      .replace(/\{\{organization_name\}\}/g, 'Acme Corp')
                      .replace(/\{\{sender_name\}\}/g, 'Marketing Team')
                      .replace(/\{\{current_date\}\}/g, fmt.short(new Date()))
                      .replace(/\{\{unsubscribe_link\}\}/g, '[Unsubscribe]')}
                  </div>
                </div>

                {previewTemplate.variable_fields.length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">{t('admin.campaignTemplates.variablesUsed', 'Variables Used')}</p>
                    <div className="flex flex-wrap gap-2">
                      {previewTemplate.variable_fields.map((field) => (
                        <span
                          key={field}
                          className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded"
                        >
                          {`{{${field}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                {t('admin.campaignTemplates.close', 'Close')}
              </button>
              {previewTemplate && (
                <button
                  type="button"
                  onClick={() => {
                    setShowPreviewModal(false);
                    handleEdit(previewTemplate);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  {t('admin.campaignTemplates.editTemplate', 'Edit Template')}
                </button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CampaignTemplates;
